// THE SHARED STAFF CHANGE-NUDGE CLIENT (card #3562, docs/adr/0003, server half
// card #3561: apps/score-api/src/ws-connection.js, lib/change-nudge.js).
//
// One WebSocket per staff screen, to the score-api WebSocket (the SAM
// `WebSocketUrl` output). It carries only a small "something moved" nudge; the
// screen answers it with the same small reload its change check would have done.
// The socket is an ADDITION: with it down or never opened the change check still
// runs, at the normal rate, so a dead socket costs speed, never correctness.
//
// Connect:   <url>?token=<Cognito access token>&orgId=<org>. A browser WebSocket
//            cannot set headers, hence the query string (the server never logs
//            it). The token is read afresh at every (re)connect.
// Subscribe: right after open, { action: "subscribe", showId, ring? }. The reply
//            is the connect answer FOR THAT SHOW:
//              { type: "subscribed" }               -> healthy
//              { type: "stay-on-change-checks" }    -> Instant updates is OFF
//                (or the show is not ours): release the socket, stay on change
//                checks at the normal rate. The server sends the same message
//                to an open socket when the setting is turned OFF later.
// Heartbeat: { action: "ping" } every HEARTBEAT_MS, answered { type: "pong" }.
//            HEALTHY = connected AND subscribed AND some message (pong, nudge,
//            anything) seen inside HEARTBEAT_TIMEOUT_MS. A silent socket (a
//            sleeping tablet, a proxy that swallowed the close) is therefore
//            DOWN, not "healthy but quiet".
// Reconnect: exponential backoff with jitter, 1 s up to 60 s, re-sending
//            subscribe on every open (a reconnect is a new connection id with no
//            subscription).
// Re-enable: the server sends NO signal when Instant updates is switched back
//            ON. While released, `probe()` (called by the store's
//            notifyShowMoved when a change check sees the show move) reconnects
//            and re-subscribes, throttled to one try per PROBE_MIN_MS.
import { emitNudge, registerProber, setSocketHealthy } from "./nudgeStore";

export const HEARTBEAT_MS = 20 * 1000;
export const HEARTBEAT_TIMEOUT_MS = 50 * 1000;
export const CONNECT_TIMEOUT_MS = 15 * 1000;
export const WATCHDOG_MS = 5 * 1000;
export const BACKOFF_MIN_MS = 1000;
export const BACKOFF_MAX_MS = 60 * 1000;
export const PROBE_MIN_MS = 2 * 60 * 1000;

// status: "idle" (not started / stopped), "connecting", "healthy", "down"
// (reconnecting), "released" (told to stay on change checks: no socket held),
// "asleep" (the page is hidden: no socket held, reopened when it is shown again).
// A hidden tab throttles timers to about one a minute, so a held socket's 20 s
// ping / 50 s timeout would flap every minute; change checks stop while hidden
// anyway, so there is nothing for the socket to speed up.
const isHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

export function createNudgeSocket({
  url,
  getAuth,
  showId,
  ring,
  onStatus,
  WebSocketImpl,
  random = Math.random,
}) {
  let ws = null;
  let status = "idle";
  let stopped = true;
  let attempts = 0;
  let lastSeen = 0;
  let lastProbeAt = -Infinity;
  let reconnectTimer = null;
  let heartbeatTimer = null;
  let watchdogTimer = null;
  let unregisterProber = null;
  let reason = null;

  const setStatus = (next) => {
    if (status === next) return;
    status = next;
    setSocketHealthy(showId, next === "healthy");
    onStatus?.(next, reason);
  };

  const clearTimers = () => {
    if (reconnectTimer != null) clearTimeout(reconnectTimer);
    if (heartbeatTimer != null) clearInterval(heartbeatTimer);
    if (watchdogTimer != null) clearInterval(watchdogTimer);
    reconnectTimer = heartbeatTimer = watchdogTimer = null;
  };

  const closeSocket = () => {
    const old = ws;
    ws = null;
    if (!old) return;
    old.onopen = old.onmessage = old.onclose = old.onerror = null;
    try {
      old.close();
    } catch {
      // already closed
    }
  };

  const sendJson = (obj) => {
    try {
      ws?.send(JSON.stringify(obj));
    } catch {
      // a send on a dying socket: the watchdog / onclose handles it
    }
  };

  const goDown = () => {
    clearTimers();
    closeSocket();
    if (stopped) return;
    setStatus("down");
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempts);
    attempts += 1;
    const delay = Math.round(base * (0.8 + random() * 0.4));
    reconnectTimer = setTimeout(connect, delay);
  };

  const onVisibility = () => {
    if (stopped) return;
    if (isHidden()) {
      if (status === "released") return;
      clearTimers();
      closeSocket();
      attempts = 0;
      setStatus("asleep");
    } else if (status === "asleep") {
      connect();
    }
  };

  const release = (why) => {
    clearTimers();
    closeSocket();
    reason = why ?? null;
    attempts = 0;
    // Throttle the first re-ask from the moment we were told to stay put.
    lastProbeAt = Date.now();
    setStatus("released");
  };

  function connect() {
    reconnectTimer = null;
    if (stopped || !url || !showId) return;
    if (isHidden()) {
      setStatus("asleep");
      return;
    }
    const auth = getAuth?.() ?? {};
    if (!auth.token) {
      // Not signed in yet: nothing to connect with. Try again on the same ladder.
      goDown();
      return;
    }
    const Impl = WebSocketImpl ?? globalThis.WebSocket;
    if (!Impl) return;
    clearTimers();
    closeSocket();
    setStatus("connecting");
    const q = new URLSearchParams({ token: auth.token });
    if (auth.orgId) q.set("orgId", auth.orgId);
    let sock;
    try {
      sock = new Impl(`${url}${url.includes("?") ? "&" : "?"}${q.toString()}`);
    } catch {
      goDown();
      return;
    }
    ws = sock;
    lastSeen = Date.now();
    sock.onopen = () => {
      if (ws !== sock) return;
      lastSeen = Date.now();
      sendJson({ action: "subscribe", showId, ...(ring ? { ring } : {}) });
    };
    sock.onmessage = (ev) => {
      if (ws !== sock) return;
      lastSeen = Date.now();
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg?.type === "subscribed") {
        attempts = 0;
        reason = null;
        setStatus("healthy");
      } else if (msg?.type === "stay-on-change-checks") {
        release(msg.reason);
      } else if (msg?.type === "change") {
        if (status === "healthy") emitNudge(showId);
      }
      // "pong" and anything else: seen, nothing more to do.
    };
    sock.onclose = () => {
      if (ws !== sock) return;
      goDown();
    };
    sock.onerror = () => {
      // onclose follows; the watchdog covers a socket that errors and never closes.
    };
    heartbeatTimer = setInterval(() => {
      if (status === "healthy") sendJson({ action: "ping" });
    }, HEARTBEAT_MS);
    watchdogTimer = setInterval(() => {
      const limit = status === "healthy" ? HEARTBEAT_TIMEOUT_MS : CONNECT_TIMEOUT_MS;
      if (Date.now() - lastSeen > limit) goDown();
    }, WATCHDOG_MS);
  }

  function probe() {
    if (stopped || status !== "released" || isHidden()) return;
    const now = Date.now();
    if (now - lastProbeAt < PROBE_MIN_MS) return;
    lastProbeAt = now;
    connect();
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      attempts = 0;
      unregisterProber = registerProber(showId, probe);
      if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
      connect();
    },
    stop() {
      stopped = true;
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
      clearTimers();
      closeSocket();
      unregisterProber?.();
      unregisterProber = null;
      status = "idle";
      setSocketHealthy(showId, false);
    },
    probe,
    get status() {
      return status;
    },
  };
}
